import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Check, Sparkles, Lock, Globe, Mail, Shield, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Layout } from '@/components/wiki/Layout';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ClaimPage() {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const [page, setPage] = useState(null);
  const [pricing, setPricing] = useState({ monthly_price: 69.00, yearly_price: 599.00 });
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('yearly');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pageResponse, pricingResponse] = await Promise.all([
          axios.get(`${API}/pages/id/${pageId}`),
          axios.get(`${API}/pricing`)
        ]);
        setPage(pageResponse.data);
        setPricing(pricingResponse.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Page not found');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [pageId]);

  const monthlyEquivalent = (pricing.yearly_price / 12).toFixed(2);
  const savings = ((pricing.monthly_price * 12) - pricing.yearly_price).toFixed(2);
  const savingsPercent = Math.round(((pricing.monthly_price * 12 - pricing.yearly_price) / (pricing.monthly_price * 12)) * 100);

  const handleCheckout = async (planType) => {
    if (!user) {
      toast.error('Please log in to claim a page');
      navigate('/login');
      return;
    }

    setProcessing(true);
    setSelectedPlan(planType);
    
    try {
      const response = await axios.post(`${API}/subscriptions/checkout`, {
        page_id: pageId,
        plan_type: planType,
        origin_url: window.location.origin
      }, { headers: getAuthHeader() });
      
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-16">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-[#242424] rounded w-1/2 mx-auto" />
            <div className="h-96 bg-[#242424] rounded" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-12" data-testid="claim-page">
        {/* Back Link */}
        {page && (
          <Link to={`/wiki/${page.slug}`} className="inline-flex items-center gap-2 text-[#D4AF37] hover:underline mb-8">
            <ArrowLeft className="h-4 w-4" />
            Back to {page.title}
          </Link>
        )}

        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C5A028] text-[#1A1A1A] mb-6">
            <Sparkles className="h-8 w-8" />
          </div>
          <h1 className="font-spectral text-3xl sm:text-4xl font-bold text-white mb-4">
            Claim "{page?.title}"
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Take control of your Handshake profile. Add official information, social links, and protect your page from unauthorized edits.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {/* Monthly */}
          <div 
            className={`bg-[#242424] rounded-2xl border-2 p-8 transition-all cursor-pointer ${
              selectedPlan === 'monthly' ? 'border-[#D4AF37] shadow-lg shadow-[#D4AF37]/10' : 'border-[#333333] hover:border-[#444444]'
            }`}
            onClick={() => setSelectedPlan('monthly')}
            data-testid="monthly-plan-card"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-white">Monthly</h3>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                selectedPlan === 'monthly' ? 'border-[#D4AF37] bg-[#D4AF37]' : 'border-gray-500'
              }`}>
                {selectedPlan === 'monthly' && <Check className="h-3 w-3 text-[#1A1A1A]" />}
              </div>
            </div>
            <div className="mb-6">
              <span className="text-4xl font-bold text-white">${pricing.monthly_price}</span>
              <span className="text-gray-400">/month</span>
            </div>
            <Button 
              onClick={() => handleCheckout('monthly')}
              disabled={processing}
              className={`w-full ${selectedPlan === 'monthly' ? 'bg-[#D4AF37] hover:bg-[#C5A028] text-[#1A1A1A]' : 'bg-[#333333] text-white hover:bg-[#444444]'}`}
              data-testid="monthly-checkout-btn"
            >
              {processing && selectedPlan === 'monthly' ? 'Processing...' : 'Get Monthly'}
            </Button>
          </div>

          {/* Yearly */}
          <div 
            className={`bg-[#242424] rounded-2xl border-2 p-8 relative transition-all cursor-pointer ${
              selectedPlan === 'yearly' ? 'border-[#D4AF37] shadow-lg shadow-[#D4AF37]/10' : 'border-[#333333] hover:border-[#444444]'
            }`}
            onClick={() => setSelectedPlan('yearly')}
            data-testid="yearly-plan-card"
          >
            {/* Best Value Badge */}
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="bg-gradient-to-r from-[#D4AF37] to-[#C5A028] text-[#1A1A1A] text-xs font-bold px-4 py-1 rounded-full">
                BEST VALUE - SAVE {savingsPercent}%
              </span>
            </div>
            
            <div className="flex items-center justify-between mb-4 mt-2">
              <h3 className="font-semibold text-white">Yearly</h3>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                selectedPlan === 'yearly' ? 'border-[#D4AF37] bg-[#D4AF37]' : 'border-gray-500'
              }`}>
                {selectedPlan === 'yearly' && <Check className="h-3 w-3 text-[#1A1A1A]" />}
              </div>
            </div>
            <div className="mb-2">
              <span className="text-4xl font-bold text-white">${pricing.yearly_price}</span>
              <span className="text-gray-400">/year</span>
            </div>
            <p className="text-sm text-gray-400 mb-4">
              Just ${monthlyEquivalent}/month • Save ${savings}/year
            </p>
            <Button 
              onClick={() => handleCheckout('yearly')}
              disabled={processing}
              className="w-full bg-gradient-to-r from-[#D4AF37] to-[#C5A028] hover:from-[#C5A028] hover:to-[#B49520] text-[#1A1A1A]"
              data-testid="yearly-checkout-btn"
            >
              {processing && selectedPlan === 'yearly' ? 'Processing...' : 'Get Yearly'}
            </Button>
          </div>
        </div>

        {/* Features */}
        <div className="bg-[#242424] rounded-2xl border border-[#333333] p-8 mb-12">
          <h3 className="font-spectral text-xl font-bold text-white mb-6 text-center">What You Get</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Globe, title: 'Website Links', desc: 'Add links to your official website' },
              { icon: Mail, title: 'Contact Info', desc: 'Display email, phone & address' },
              { icon: Lock, title: 'Page Lock', desc: 'Prevent unauthorized edits' },
              { icon: Shield, title: 'Sponsored Badge', desc: 'Show authenticity to visitors' },
            ].map((feature) => (
              <div key={feature.title} className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] mb-3">
                  <feature.icon className="h-6 w-6" />
                </div>
                <h4 className="font-semibold text-white mb-1">{feature.title}</h4>
                <p className="text-sm text-gray-400">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="bg-[#1A1A1A] rounded-2xl border border-[#333333] p-8">
          <h3 className="font-spectral text-xl font-bold text-white mb-6">Frequently Asked Questions</h3>
          <div className="space-y-4">
            <div>
              <h4 className="font-medium text-white mb-1">Can I cancel anytime?</h4>
              <p className="text-sm text-gray-400">Yes, you can cancel your subscription at any time. Your benefits will continue until the end of your billing period.</p>
            </div>
            <div>
              <h4 className="font-medium text-white mb-1">What happens to my page if I cancel?</h4>
              <p className="text-sm text-gray-400">Your page remains on Handshake but loses sponsor benefits (contact info, social links, page lock, and sponsored badge).</p>
            </div>
            <div>
              <h4 className="font-medium text-white mb-1">Can I claim multiple pages?</h4>
              <p className="text-sm text-gray-400">Yes! Each page requires its own subscription. Contact us for volume discounts on multiple pages.</p>
            </div>
          </div>
        </div>

        {/* Login Prompt */}
        {!user && (
          <div className="mt-8 text-center p-6 bg-[#242424] border border-[#D4AF37]/30 rounded-xl">
            <p className="text-gray-300">
              Please <Link to="/login" className="text-[#D4AF37] underline font-medium">log in</Link> or{' '}
              <Link to="/register" className="text-[#D4AF37] underline font-medium">register</Link> to claim this page.
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}
